import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {ATTENDANCE_MODEL, NO_DEPARTMENT} from "../Model/Model";
import HandleChange from "../../../../../Helpers/HandleChange";
import { useTranslation } from "react-i18next";
import { getDepartments } from "../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import { useDispatch, useSelector } from "react-redux";
import { GetAddressFromLatLng } from "../../../../../reduxToolkit/Address/AddressApi";
import { useNavigate } from "react-router-dom";
import { GetAllLocationsByDepartment } from "../../../../../reduxToolkit/EmployeePayRoll/EmployeePayRollApi";
import { SEARCH_MODEL } from "../../../../../components/table/Model/TableModel";
import {
    DEFAULT_LATITUDE,
    DEFAULT_LONGITUDE,
    errorMsg,
    getDateLongAsIs,
    tryGetAddress
} from "../../../../../Apis/functions";
import TableFunction from "../../../../../components/table/TableFunction";
import { VIEW_TYPE } from "../../../../../enums/ViewType";
import { SimpleSearch, SimpleSearchWithId } from "../../../../../reduxToolkit/Search/SearchApi";
import { TABLES } from "../../../../../Apis/Tables";
import {debounce} from "lodash";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import {DetailsIconsOptions, IconMoreVerticalDots} from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * Custom hook to manage state and behavior for the current attendance map view.
 *
 * Handles:
 * - Employee and department data fetching
 * - Map latitude/longitude state
 * - Address lookup based on coordinates
 * - Detail modal toggling
 * - Input change handling
 *
 * @param {Object} props - Hook parameters
 * @param {string} props.option - Search model option (e.g., TABLES.ATTENDANCE)
 */

export const AttendanceCurrentMapApp =({option})=>{
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const navigate = useNavigate();
    const [item , setItem] = useState({...ATTENDANCE_MODEL})
    const { allEmployeesList } = useSelector((state) => state.EmployeeRolesSlice);
    const [departments, setDepartments] = useState([]);
    const [idEmployee, setIdEmployee] = useState();
    const [direction, setDirection] = useState();
    const [locations, setLocations] = useState([]);
    const [attendanceByDepartment, setAttendanceByDepartment] = useState([]);
    const [attendanceByEmployee, setAttendanceByEmployee] = useState([]);
    const [attendanceEmployee, setAttendanceEmployee] = useState();
    const [showDetailModal, setShowDetailsModal] = useState(false);
    const [selectEmployee, setSelectEmployee] = useState(null);
    const [cleanAutocompleteSearch, setCleanAutocompleteSearch] = useState(false);
    const [searchLocation, setSearchLocation] = useState("");
    const elementRef = useRef(null);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const dispatch = useDispatch();
    const minEndDate = item?.startDate ? new Date(new Date(item.startDate).getTime() + 30 * 60 * 1000) : null;
    const [loadingMore, setLoadingMore] = useState(false);
    const listContainerRef = useRef(null);
    const [searchType, setSearchType] = useState(TABLES.DEPARTMENT);
    const { t } = useTranslation();
    const timeoutRef = useRef(null);

    /** Initialization */
    useEffect(() => {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        setItem((prevState) => ({
            ...prevState,
            latitude: DEFAULT_LATITUDE,
            longitude: DEFAULT_LONGITUDE,
            startDate: startOfDay.getTime(),
            endDate: endOfDay.getTime(),
        }));

        dispatch(getDepartments()).then(({ payload: { data: { data, success } } }) => {
            if (success) setDepartments([
                {
                    id: NO_DEPARTMENT,
                    name: t('no_department')
                },
                ...data,
            ]);
        });
    }, []);

    /** Get direction from coordinates */
    const getLocationData = (lat, lng) => {
        dispatch(GetAddressFromLatLng({ lat, lng, location: 7 })).then(({ payload }) => {
            if (payload?.success) {
                setDirection(payload?.location);
            }
        });
    };

    /** Assign lat/lng */
    useEffect(() => {
        getLocationData(item?.latitude, item?.longitude);
    }, [item]);

    /** Adjust top value for map element */
    useLayoutEffect(() => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const distanceTop = rect.top + 0;
            elementRef.current.style.setProperty("--top-value", `${distanceTop}px`);
        }
    }, [item]);

    useEffect(() => setSearchLocation(""), []);
    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    const getDepartmentId = () => {
        if(!item?.departmentId) return null;

      return item?.departmentId === NO_DEPARTMENT ? null : item?.departmentId
    }
    /** Fetch by Department */
    const handleSearch = async () => {
        setPage(0);
        setSearchType(TABLES.DEPARTMENT);
        setAttendanceByDepartment([]);
        setAttendanceByEmployee([]);
        setCleanAutocompleteSearch(true);

        const body = {
            departmentId: getDepartmentId(),
            startDate: getDateLongAsIs(item?.startDate),
            endDate: getDateLongAsIs(item?.endDate)
        };

        const bodyList = SEARCH_MODEL.setObject({
            page: 0,
            size: rowsPerPage,
            model: TABLES.ATTENDANCE_MAP,
            modelId: getDepartmentId(),
            attendanceStartDate: getDateLongAsIs(item?.startDate),
            attendanceEndDate: getDateLongAsIs(item?.endDate),
        });

        try {
            const [locationsRes, searchRes] = await Promise.all([
                dispatch(GetAllLocationsByDepartment({ body })).unwrap(),
                dispatch(SimpleSearch({ body: bodyList })).unwrap(),
            ]);

            if (locationsRes.data.success) setLocations(locationsRes.data.data);
            else errorMsg(locationsRes.data.code);

            if (searchRes.data.success) {
                setAttendanceByDepartment(searchRes.data.data);
            } else errorMsg(searchRes.data.code);
        } catch (err) {
            errorMsg(err);
        }
    };

    const { page, rowsPerPage, setPage,openMenu, handleMenuToggle, handleClose } = TableFunction({
        data: searchType === TABLES.USER ? attendanceByEmployee : attendanceByDepartment,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: "attendance_per_page",
        fixedPagination: false,
        hasContainer: true
    });

    const bodyDepartment = {
        pagination: { page: 0, size: 20 },
        model: TABLES.EMPLOYEES,
        modelId: getDepartmentId(),
    };

    /** Fetch by Employee */
    useEffect(() => {
        if (selectEmployee?.userId) {
            setPage(0);
            setAttendanceByEmployee([]);
            setAttendanceByDepartment([]);
            setSearchType(TABLES.USER);

            const body = SEARCH_MODEL.setObject({
                page: 0,
                size: rowsPerPage,
                model: option,
                userId: selectEmployee.userId,
                attendanceStartDate: getDateLongAsIs(item?.startDate),
                attendanceEndDate: getDateLongAsIs(item?.endDate),
            });

            dispatch(SimpleSearchWithId({ body })).then(
                ({ payload: { data: { data, success, code } } }) => {
                    if (success) {
                        setAttendanceByEmployee(data);
                        const bodyLoc = {
                            userId: selectEmployee?.userId,
                            startDate: getDateLongAsIs(item?.startDate),
                            endDate: getDateLongAsIs(item?.endDate)
                        };

                        dispatch(GetAllLocationsByDepartment({ body: bodyLoc })).then(
                            ({ payload: { data: { data, success, code } } }) => {
                                if (success) setLocations(data);
                                else errorMsg(code);
                            }
                        );
                    } else errorMsg(code);
                }
            );
        }
    }, [selectEmployee]);

    /** Infinite scroll */
    const loadMoreData = async () => {
        if (loadingMore) return;

        const activeList = searchType === TABLES.USER ? attendanceByEmployee : attendanceByDepartment;
        const hasMore = !activeList?.last && activeList?.totalElements > activeList?.content?.length;

        if (!hasMore) return;

        setLoadingMore(true);
        const nextPage = page + 1;
        setPage(nextPage);

        try {
            if (searchType === TABLES.USER && selectEmployee?.userId) {
                const body = SEARCH_MODEL.setObject({
                    page: nextPage,
                    size: rowsPerPage,
                    model: option,
                    userId: selectEmployee?.userId,
                    attendanceStartDate: getDateLongAsIs(item?.startDate),
                    attendanceEndDate: getDateLongAsIs(item?.endDate),
                });
                dispatch(SimpleSearchWithId({ body })).then(({ payload: { data: { data, success, code } } }) => {
                    if (success) {
                        setAttendanceByEmployee((prev) => ({
                            ...data,
                            content: [...prev.content, ...data.content],
                        }));
                    } else errorMsg(code);
                });
            } else {
                const bodyList = SEARCH_MODEL.setObject({
                    page: nextPage,
                    size: rowsPerPage,
                    model: TABLES.ATTENDANCE_MAP,
                    modelId: getDepartmentId(),
                    attendanceStartDate: getDateLongAsIs(item?.startDate),
                    attendanceEndDate: getDateLongAsIs(item?.endDate),
                });
                const response = await dispatch(SimpleSearch({ body: bodyList })).unwrap();
                if (response.data.success) {
                    setAttendanceByDepartment((prev) => ({
                        ...response.data.data,
                        content: [...prev.content, ...response.data.data.content],
                    }));
                } else errorMsg(response.data.code);
            }
        } catch (err) {
            errorMsg(err);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        const container = listContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop <= clientHeight + 80;

            const activeList = searchType === TABLES.USER ? attendanceByEmployee : attendanceByDepartment;
            const hasMore = activeList && !activeList?.last && activeList?.totalElements > activeList?.content?.length;

            if (isNearBottom && hasMore && !loadingMore) {
                loadMoreData();
            }
        };

        container.addEventListener("scroll", handleScroll);

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [attendanceByDepartment, attendanceByEmployee, loadingMore, page, rowsPerPage, searchType]);

    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setDirection);
    }, 800);

    const getActionsButtons = (item, index) => {
        return (
            <>
                <DropDownMenuProfile openMenu={openMenu}
                                     handleMenuToggle={handleMenuToggle}
                                     menuId={`menu-${item.id}`}
                                     icon={<IconMoreVerticalDots />}
                                     classNameIconContainer={"p-0 m-0" }
                >
                    <DropDownMenuItem
                        id={`dmi_acm_option_${item.id}`}
                        index={index}
                        classNameContainerIcon={"m-0 p-0"}
                        handleClose={handleClose}
                        name={t('details')}
                        icon={<DetailsIconsOptions />}
                        click={() => {
                            if (item?.id) setIdEmployee(item?.userId)
                            setShowDetailsModal(true)
                        }}
                    />
                </DropDownMenuProfile>
            </>
        );
    };

    /** Export */
    return {
        item,
        setItem,
        searchLocation,
        setSearchLocation,
        elementRef,
        handleChange,
        departments,
        employees: allEmployeesList,
        selectEmployee,
        setSelectEmployee,
        t,
        direction,
        setShowDetailsModal,
        showDetailModal,
        handleSearch,
        open,
        setOpen,
        options,
        setOptions,
        navigate,
        locations,
        minEndDate,
        idEmployee,
        setIdEmployee,
        dispatch,
        attendanceEmployee,
        setAttendanceEmployee,
        attendanceByDepartment,
        attendanceByEmployee,
        bodyDepartment,
        loadingMore,
        listContainerRef,
        searchType,
        handleMapMove,
        getActionsButtons,
        cleanAutocompleteSearch,
        setCleanAutocompleteSearch
    };
};
